import { Component, HostListener, inject, signal } from '@angular/core';
import { TooltipModule } from '@openng/optimus-ui/tooltip';

import { AiChatDockService } from '@app/shared/services/ai-chat-dock.service';

const STORAGE_KEY = 'corra:handle-top';
const HANDLE_HEIGHT = 32;
const DRAG_THRESHOLD = 5;

@Component({
    selector: 'cor-ai-chat-handle',
    templateUrl: './ai-chat-handle.component.html',
    styleUrls: ['./ai-chat-handle.component.scss'],
    imports: [TooltipModule],
})
export class AiChatHandleComponent {
    protected readonly aiChatDockService = inject(AiChatDockService);
    protected readonly botName = this.aiChatDockService.botName;
    protected readonly isVisible = this.aiChatDockService.isVisible;
    protected readonly topPx = signal<number>(this.loadTop());

    private isDragging = false;
    private pointerStartY = 0;
    private handleStartTop = 0;
    private hasMoved = false;

    @HostListener('window:resize')
    protected onResize(): void {
        this.topPx.update(top => this.clamp(top));
    }

    protected onPointerDown(event: PointerEvent): void {
        this.isDragging = true;
        this.hasMoved = false;
        this.pointerStartY = event.clientY;
        this.handleStartTop = this.topPx();
        (event.target as HTMLElement).setPointerCapture(event.pointerId);
        event.preventDefault();
    }

    protected onPointerMove(event: PointerEvent): void {
        if (!this.isDragging) {
            return;
        }

        const delta = event.clientY - this.pointerStartY;
        if (Math.abs(delta) > DRAG_THRESHOLD) {
            this.hasMoved = true;
        }
        this.topPx.set(this.clamp(this.handleStartTop + delta));
    }

    protected onPointerUp(event: PointerEvent): void {
        if (!this.isDragging) {
            return;
        }

        this.isDragging = false;
        (event.target as HTMLElement).releasePointerCapture?.(event.pointerId);

        if (this.hasMoved) {
            this.persist();
        } else {
            this.aiChatDockService.toggle();
        }
    }

    private loadTop(): number {
        const stored = Number(localStorage.getItem(STORAGE_KEY));
        const fallback = window.innerHeight / 2 - HANDLE_HEIGHT / 2;

        return this.clamp(Number.isFinite(stored) && stored > 0 ? stored : fallback);
    }

    private clamp(top: number): number {
        const max = Math.max(window.innerHeight - HANDLE_HEIGHT, 0);

        return Math.min(Math.max(top, 0), max);
    }

    private persist(): void {
        localStorage.setItem(STORAGE_KEY, String(this.topPx()));
    }
}
